import React, { useState } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    Alert,
    LinearProgress,
    List,
    ListItem,
    ListItemText,
    Paper,
    Divider,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
} from "@mui/material";
import {
    CloudUpload as UploadIcon,
    CheckCircle as SuccessIcon,
    Error as ErrorIcon,
    Info as InfoIcon,
    Warning as WarningIcon,
    Check as CheckIcon,
} from "@mui/icons-material";
import * as XLSX from "xlsx";
import api from '../config/api';
import useStore from "../config/store";
import { useTranslation } from "react-i18next";

const ApplicantBulkUploadModal = ({ open, onClose, onSuccess }) => {
    const [file, setFile] = useState(null);
    const [loading, setLoading] = useState(false);
    const [uploadResult, setUploadResult] = useState(null);
    const [error, setError] = useState(null);
    const [previewData, setPreviewData] = useState(null);
    const [step, setStep] = useState(1);

    const { authToken, showAlert, project, language } = useStore.getState();
    const { t } = useTranslation();

    // Nombre por PARTES (regla 2026-08-14), igual que el modal individual:
    // nombres + apellido paterno obligatorios, apellido materno opcional. El
    // nombre completo lo compone el backend. A propósito NO se acepta una
    // columna "apellidos" con los dos juntos: partirla es adivinar, y los
    // apellidos compuestos ("de la Fuente") lo hacen imposible.
    // El formato ANTIGUO —una sola columna "nombre" con todo junto— se
    // rechaza (decisión Erwin 2026-09-16): creaba registros sin partes, que el
    // bot y el ERP tratan como incompletos.
    const ALLOWED_COLUMNS = {
        nombres: "names",
        nombre: "names",
        names: "names",
        first_name: "names",
        apellido_paterno: "firstLastName",
        primer_apellido: "firstLastName",
        first_last_name: "firstLastName",
        last_name: "firstLastName",
        apellido_materno: "secondLastName",
        segundo_apellido: "secondLastName",
        second_last_name: "secondLastName",
        email: "email",
        telefono: "phone",
        phone: "phone",
        phone_number: "phone",
        rut: "rut",
        dni: "rut",
        id: "rut",
        identifier: "rut",
        genero: "gender",
        gender: "gender",
        sexo: "gender",
        pais: "country",
        country: "country",
        profesion: "profession",
        profession: "profession",
        cargo: "currentPosition",
        cargo_actual: "currentPosition",
        current_position: "currentPosition",
        position: "currentPosition",
        puesto: "currentPosition",
    };

    const handleFileChange = (event) => {
        const selectedFile = event.target.files[0];
        if (selectedFile) {
            const validTypes = [
                "application/vnd.ms-excel",
                "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            ];
            if (!validTypes.includes(selectedFile.type)) {
                setError(t('applicants.bulk.invalidFileType'));
                setFile(null);
                return;
            }
            setFile(selectedFile);
            setError(null);
            setUploadResult(null);
            setPreviewData(null);
            setStep(1);
        }
    };

    const processExcelFile = (file) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();

            reader.onload = (e) => {
                try {
                    const data = new Uint8Array(e.target.result);
                    const workbook = XLSX.read(data, { type: "array" });

                    const firstSheetName = workbook.SheetNames[0];
                    const worksheet = workbook.Sheets[firstSheetName];

                    const jsonData = XLSX.utils.sheet_to_json(worksheet);

                    if (jsonData.length === 0) {
                        reject(new Error(t('applicants.bulk.emptyFile')));
                        return;
                    }

                    // Obtener TODAS las columnas únicas de TODOS los registros
                    const fileColumnsSet = new Set();
                    jsonData.forEach(row => {
                        Object.keys(row).forEach(col => {
                            if (col !== '__rowNum__') { // Ignorar columnas internas de xlsx
                                fileColumnsSet.add(col);
                            }
                        });
                    });
                    const fileColumns = Array.from(fileColumnsSet);

                    const normalizedColumns = fileColumns.map(col =>
                        col.toLowerCase().trim().replace(/\s+/g, '_')
                    );

                    const invalidColumns = normalizedColumns.filter(
                        col => !ALLOWED_COLUMNS.hasOwnProperty(col)
                    );

                    if (invalidColumns.length > 0) {
                        reject(
                            new Error(
                                `${t('applicants.bulk.invalidColumns')} ${invalidColumns.join(", ")}. ${t('applicants.bulk.onlyAllowed')}`
                            )
                        );
                        return;
                    }

                    const detectedColumns = {
                        names: false,
                        firstLastName: false,
                        secondLastName: false,
                        email: false,
                        phone: false,
                        rut: false,
                        gender: false,
                        country: false,
                        profession: false,
                        currentPosition: false,
                    };

                    const columnMapping = {};
                    fileColumns.forEach((originalCol, index) => {
                        const normalizedCol = normalizedColumns[index];
                        const mappedField = ALLOWED_COLUMNS[normalizedCol];
                        if (mappedField) {
                            detectedColumns[mappedField] = true;
                            columnMapping[originalCol] = mappedField;
                        }
                    });

                    // Sin apellido paterno es el archivo del formato antiguo
                    // (todo el nombre en una columna): se rechaza con la pista
                    if (!detectedColumns.names || !detectedColumns.firstLastName) {
                        reject(new Error(t('applicants.bulk.missingNameColumn')));
                        return;
                    }

                    if (!detectedColumns.email) {
                        reject(new Error(t('applicants.bulk.missingEmailColumn')));
                        return;
                    }

                    const applicants = jsonData.map((row, index) => {
                        const applicant = {
                            names: "",
                            firstLastName: "",
                            secondLastName: "",
                            email: "",
                            phone: "",
                            rut: "",
                            gender: "",
                            country: "",
                            profession: "",
                            currentPosition: "",
                        };

                        Object.keys(row).forEach((originalCol) => {
                            const mappedField = columnMapping[originalCol];
                            const value = row[originalCol];

                            console.log(`  Column: "${originalCol}", Value: "${value}", Type: ${typeof value}, MappedField: "${mappedField}", HasValue: ${!!(mappedField && value)}`);

                            if (mappedField && value) {
                                applicant[mappedField] = String(value).trim();
                                console.log(`    ✅ Assigned: ${mappedField} = "${applicant[mappedField]}"`);
                            }
                        });

                        if (applicant.email) {
                            applicant.email = applicant.email.toLowerCase();
                        }

                        return applicant;
                    });

                    const validApplicants = applicants.filter(
                        (applicant) => applicant.names && applicant.firstLastName && applicant.email
                    );

                    if (validApplicants.length === 0) {
                        reject(new Error(t('applicants.bulk.noValidRecords')));
                        return;
                    }

                    if (validApplicants.length > 3500) {
                        reject(
                            new Error(
                                `${t('applicants.bulk.exceedsLimit')} ${validApplicants.length} ${t('applicants.bulk.validRecords')}`
                            )
                        );
                        return;
                    }

                    resolve({
                        applicants: validApplicants,
                        detectedColumns,
                        totalRows: jsonData.length,
                        validRows: validApplicants.length,
                        originalColumns: fileColumns,
                    });
                } catch (err) {
                    reject(new Error(`${t('applicants.bulk.processingError')} ${err.message}`));
                }
            };

            reader.onerror = () => {
                reject(new Error(t('applicants.bulk.readError')));
            };

            reader.readAsArrayBuffer(file);
        });
    };
    const handleProcessFile = async () => {
        if (!file) {
            setError(t('applicants.bulk.selectFile'));
            return;
        }

        try {
            setLoading(true);
            setError(null);

            const processedData = await processExcelFile(file);
            setPreviewData(processedData);
            setStep(2);
        } catch (err) {
            console.error("Error processing file:", err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleConfirmUpload = async () => {
        if (!previewData) return;

        try {
            setLoading(true);
            setError(null);

            const response = await api.post(
                "/applicants/bulk",
                {
                    projectId: project,
                    applicants: previewData.applicants,
                },
                {
                    headers: { Authorization: `Bearer ${authToken}` },
                }
            );

            if (
                response.data.response &&
                response.data.response.status_response === "OK"
            ) {
                const result = response.data.response.data;
                setUploadResult(result);
                setStep(3);
                showAlert(
                    `${t('applicants.bulk.uploadCompleted')} ${result.summary.created} ${t('applicants.bulk.created')}, ${result.summary.failed} ${t('applicants.bulk.failed')}`,
                    "success"
                );
                if (onSuccess) {
                    onSuccess();
                }
            } else {
                setError(t('applicants.bulk.bulkUploadError'));
                showAlert(t('applicants.bulk.bulkUploadError'), "error");
            }
        } catch (err) {
            console.error("Error uploading bulk applicants:", err);
            const errorMessage =
                err.response?.data?.message ||
                err.message ||
                t('applicants.bulk.bulkUploadError');
            setError(errorMessage);
            showAlert(errorMessage, "error");
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        if (!loading) {
            setFile(null);
            setError(null);
            setUploadResult(null);
            setPreviewData(null);
            setStep(1);
            onClose();
        }
    };

    const handleBack = () => {
        setStep(1);
        setPreviewData(null);
        setError(null);
    };

    const downloadTemplate = () => {
        let template;
        let sheetName;

        if (language === 'en') {
            // Plantilla en inglés
            template = [
                {
                    names: "John",
                    first_last_name: "Doe",
                    second_last_name: "",
                    email: "john.doe@example.com",
                    phone: "+1234567890",
                    dni: "12345678-9",
                    gender: "male",
                    country: "United States",
                    profession: "Engineer",
                    current_position: "Senior Developer",
                },
                {
                    names: "Jane",
                    first_last_name: "Smith",
                    second_last_name: "Miller",
                    email: "jane.smith@example.com",
                    phone: "+0987654321",
                    dni: "98765432-1",
                    gender: "female",
                    country: "Canada",
                    profession: "Accountant",
                    current_position: "Finance Manager",
                },
            ];
            sheetName = "Applicants";
        } else {
            // Plantilla en español
            template = [
                {
                    nombres: "Juan Andrés",
                    apellido_paterno: "Pérez",
                    apellido_materno: "González",
                    email: "juan.perez@example.com",
                    telefono: "+56912345678",
                    rut: "12345678-9",
                    genero: "male",
                    pais: "Chile",
                    profesion: "Ingeniero",
                    cargo_actual: "Desarrollador Senior",
                },
                {
                    nombres: "María",
                    apellido_paterno: "García",
                    apellido_materno: "",
                    email: "maria.garcia@example.com",
                    telefono: "+56987654321",
                    rut: "98765432-1",
                    genero: "female",
                    pais: "Argentina",
                    profesion: "Contador",
                    cargo_actual: "Gerente de Finanzas",
                },
            ];
            sheetName = "Postulantes";
        }

        const ws = XLSX.utils.json_to_sheet(template);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, sheetName);

        XLSX.writeFile(wb, "plantilla_postulantes.xlsx");
    };

    const getColumnLabel = (field) => {
        return t(`applicants.bulk.columnLabels.${field}`);
    };

    // Nombre para mostrar en las listas: el que devolvió el backend (ya
    // compuesto) o, para las filas que fallaron antes de crearse, las partes
    const displayName = (data) =>
        (data && data.name) ||
        [data && data.names, data && data.firstLastName, data && data.secondLastName].filter(Boolean).join(" ") ||
        t('applicants.bulk.noName');

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (loading) return;

        const droppedFile = e.dataTransfer.files[0];
        if (droppedFile) {
            const validTypes = [
                "application/vnd.ms-excel",
                "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            ];
            if (!validTypes.includes(droppedFile.type)) {
                setError(t('applicants.bulk.invalidFileType'));
                setFile(null);
                return;
            }
            setFile(droppedFile);
            setError(null);
            setUploadResult(null);
            setPreviewData(null);
            setStep(1);
        }
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
            <DialogTitle>
                {t('applicants.bulk.title')}
                {step === 2 && ` - ${t('applicants.bulk.confirmData')}`}
                {step === 3 && ` - ${t('applicants.bulk.result')}`}
            </DialogTitle>

            <DialogContent>
                <Box sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 3 }}>
                    {step === 1 && (
                        <>
                            <Alert severity="info" icon={<InfoIcon />}>
                                <Typography variant="body2">
                                    <strong>{t('applicants.bulk.instructions')}</strong>
                                </Typography>
                                <ul style={{ margin: "8px 0", paddingLeft: "20px" }}>
                                    <li>
                                        <strong>{t('applicants.bulk.requiredColumns')}</strong> {t('applicants.bulk.requiredColumnsDesc')}
                                    </li>
                                    <li>
                                        <strong>{t('applicants.bulk.optionalColumns')}</strong> {t('applicants.bulk.optionalColumnsDesc')}
                                    </li>
                                    <li>
                                        <strong>{t('applicants.bulk.limit')}</strong> {t('applicants.bulk.limitDesc')}
                                    </li>
                                    <li>
                                        <strong>{t('applicants.bulk.onlyAllowedColumns')}</strong>
                                    </li>
                                </ul>
                            </Alert>

                            <Box>
                                <Button variant="outlined" onClick={downloadTemplate}>
                                    {t('applicants.bulk.downloadTemplate')}
                                </Button>
                            </Box>

                            {error && (
                                <Alert severity="error" icon={<ErrorIcon />}>
                                    {error}
                                </Alert>
                            )}

                            <Box onDragOver={handleDragOver} onDrop={handleDrop}>
                                <input
                                    accept=".xlsx,.xls"
                                    style={{ display: "none" }}
                                    id="bulk-upload-file"
                                    type="file"
                                    onChange={handleFileChange}
                                    disabled={loading}
                                />
                                <label htmlFor="bulk-upload-file" style={{ cursor: 'pointer' }}>
                                    <Paper
                                        variant="outlined"
                                        sx={{
                                            p: 3,
                                            textAlign: "center",
                                            cursor: "pointer",
                                            border: "2px dashed",
                                            borderColor: "primary.main",
                                            "&:hover": {
                                                bgcolor: "action.hover",
                                            },
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                display: "flex",
                                                flexDirection: "column",
                                                alignItems: "center",
                                                gap: 2,
                                            }}
                                        >
                                            <UploadIcon sx={{ fontSize: 48, color: "primary.main" }} />
                                            <Typography variant="body1">
                                                {file ? `${t('file')}: ${file.name}` : t('applicants.bulk.dragOrClick')}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {t('applicants.bulk.supportedFormats')}
                                            </Typography>
                                        </Box>
                                    </Paper>
                                </label>
                            </Box>

                            {loading && (
                                <Box>
                                    <Typography variant="body2" sx={{ mb: 1 }}>
                                        {t('applicants.bulk.processingFile')}
                                    </Typography>
                                    <LinearProgress />
                                </Box>
                            )}
                        </>
                    )}

                    {step === 2 && previewData && (
                        <>
                            <Alert severity="success" icon={<CheckIcon />}>
                                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                    {t('applicants.bulk.columnsIdentified')}
                                </Typography>
                                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                                    {Object.keys(previewData.detectedColumns).map(
                                        (field) =>
                                            previewData.detectedColumns[field] && (
                                                <Chip
                                                    key={field}
                                                    label={getColumnLabel(field)}
                                                    color="success"
                                                    size="small"
                                                    icon={<CheckIcon />}
                                                />
                                            )
                                    )}
                                </Box>
                            </Alert>

                            <Paper variant="outlined" sx={{ p: 2 }}>
                                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                    {t('applicants.bulk.columnsInFile')}
                                </Typography>
                                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                                    {previewData.originalColumns.map((col) => (
                                        <Chip key={col} label={col} size="small" variant="outlined" />
                                    ))}
                                </Box>
                            </Paper>

                            <Alert severity="info" icon={<InfoIcon />}>
                                <Typography variant="body2">
                                    <strong>{t('applicants.bulk.totalRows')}</strong> {previewData.totalRows}
                                </Typography>
                                <Typography variant="body2">
                                    <strong>{t('applicants.bulk.validRows')}</strong> {previewData.validRows}
                                </Typography>
                                {previewData.totalRows > previewData.validRows && (
                                    <Typography variant="body2" color="warning.main">
                                        <WarningIcon
                                            sx={{ fontSize: 16, verticalAlign: "middle", mr: 0.5 }}
                                        />
                                        {previewData.totalRows - previewData.validRows} {t('applicants.bulk.rowsOmitted')}
                                    </Typography>
                                )}
                            </Alert>

                            <Box>
                                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                    {t('applicants.bulk.preview')}
                                </Typography>
                                <TableContainer component={Paper} variant="outlined">
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell><strong>{t('applicants.bulk.columnLabels.names')}</strong></TableCell>
                                                <TableCell><strong>{t('applicants.bulk.columnLabels.firstLastName')}</strong></TableCell>
                                                <TableCell><strong>{t('applicants.bulk.columnLabels.secondLastName')}</strong></TableCell>
                                                <TableCell><strong>{t('applicants.bulk.columnLabels.email')}</strong></TableCell>
                                                {previewData.detectedColumns.phone && (
                                                    <TableCell><strong>{t('applicants.bulk.columnLabels.phone')}</strong></TableCell>
                                                )}
                                                {previewData.detectedColumns.rut && (
                                                    <TableCell><strong>{t('applicants.bulk.columnLabels.rut')}</strong></TableCell>
                                                )}
                                                {previewData.detectedColumns.gender && (
                                                    <TableCell><strong>{t('applicants.bulk.columnLabels.gender')}</strong></TableCell>
                                                )}
                                                {previewData.detectedColumns.country && (
                                                    <TableCell><strong>{t('applicants.bulk.columnLabels.country')}</strong></TableCell>
                                                )}
                                                {previewData.detectedColumns.profession && (
                                                    <TableCell><strong>{t('applicants.bulk.columnLabels.profession')}</strong></TableCell>
                                                )}
                                                {previewData.detectedColumns.currentPosition && (
                                                    <TableCell><strong>{t('applicants.bulk.columnLabels.currentPosition')}</strong></TableCell>
                                                )}
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {previewData.applicants.slice(0, 5).map((applicant, index) => (
                                                <TableRow key={index}>
                                                    <TableCell>{applicant.names}</TableCell>
                                                    <TableCell>{applicant.firstLastName}</TableCell>
                                                    <TableCell>{applicant.secondLastName || "-"}</TableCell>
                                                    <TableCell>{applicant.email}</TableCell>
                                                    {previewData.detectedColumns.phone && (
                                                        <TableCell>{applicant.phone || "-"}</TableCell>
                                                    )}
                                                    {previewData.detectedColumns.rut && (
                                                        <TableCell>{applicant.rut || "-"}</TableCell>
                                                    )}
                                                    {previewData.detectedColumns.gender && (
                                                        <TableCell>{applicant.gender || "-"}</TableCell>
                                                    )}
                                                    {previewData.detectedColumns.country && (
                                                        <TableCell>{applicant.country || "-"}</TableCell>
                                                    )}
                                                    {previewData.detectedColumns.profession && (
                                                        <TableCell>{applicant.profession || "-"}</TableCell>
                                                    )}
                                                    {previewData.detectedColumns.currentPosition && (
                                                        <TableCell>{applicant.currentPosition || "-"}</TableCell>
                                                    )}
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                                {previewData.validRows > 5 && (
                                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                                        {t('applicants.bulk.andMore')} {previewData.validRows - 5} {t('applicants.bulk.moreRecords')}
                                    </Typography>
                                )}
                            </Box>

                            {error && (
                                <Alert severity="error" icon={<ErrorIcon />}>
                                    {error}
                                </Alert>
                            )}

                            {loading && (
                                <Box>
                                    <Typography variant="body2" sx={{ mb: 1 }}>
                                        {t('applicants.bulk.uploadingToSystem')}
                                    </Typography>
                                    <LinearProgress />
                                </Box>
                            )}
                        </>
                    )}

                    {step === 3 && uploadResult && (
                        <Box>
                            <Alert
                                severity={uploadResult.summary.failed === 0 ? "success" : "warning"}
                                sx={{ mb: 2 }}
                            >
                                <Typography variant="subtitle2">{t('applicants.bulk.uploadSummary')}</Typography>
                                <Typography variant="body2">
                                    {t('applicants.bulk.totalLabel')} {uploadResult.summary.total} | {t('applicants.bulk.createdLabel')} {uploadResult.summary.created} | {t('applicants.bulk.failedLabel')} {uploadResult.summary.failed}
                                </Typography>
                            </Alert>

                            {uploadResult.successful.length > 0 && (
                                <Box sx={{ mb: 2 }}>
                                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                        <SuccessIcon sx={{ fontSize: 18, verticalAlign: "middle", mr: 1 }} />
                                        {t('applicants.bulk.successfullyCreated')} ({uploadResult.successful.length}):
                                    </Typography>
                                    <Paper variant="outlined" sx={{ maxHeight: 300, overflow: "auto" }}>
                                        <List dense>
                                            {uploadResult.successful.slice(0, 10).map((item, index) => (
                                                <React.Fragment key={index}>
                                                    <ListItem>
                                                        <ListItemText
                                                            primary={displayName(item.data)}
                                                            secondary={`${t('applicants.bulk.emailLabel')} ${item.data.email}`}
                                                        />
                                                    </ListItem>
                                                    {index < Math.min(9, uploadResult.successful.length - 1) && <Divider />}
                                                </React.Fragment>
                                            ))}
                                            {uploadResult.successful.length > 10 && (
                                                <ListItem>
                                                    <ListItemText secondary={`${t('applicants.bulk.andMore')} ${uploadResult.successful.length - 10} ${t('applicants.bulk.more')}`} />
                                                </ListItem>
                                            )}
                                        </List>
                                    </Paper>
                                </Box>
                            )}

                            {uploadResult.failed.length > 0 && (
                                <Box>
                                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                        <ErrorIcon sx={{ fontSize: 18, verticalAlign: "middle", mr: 1 }} color="error" />
                                        {t('applicants.bulk.withErrors')} ({uploadResult.failed.length}):
                                    </Typography>
                                    <Paper variant="outlined" sx={{ maxHeight: 300, overflow: "auto" }}>
                                        <List dense>
                                            {uploadResult.failed.slice(0, 10).map((item, index) => (
                                                <React.Fragment key={index}>
                                                    <ListItem>
                                                        <ListItemText
                                                            primary={displayName(item.data)}
                                                            secondary={`${t('applicants.bulk.errorLabel')} ${item.error} | ${t('applicants.bulk.emailLabel')} ${item.data.email || "N/A"}`}
                                                        />
                                                    </ListItem>
                                                    {index < Math.min(9, uploadResult.failed.length - 1) && <Divider />}
                                                </React.Fragment>
                                            ))}
                                            {uploadResult.failed.length > 10 && (
                                                <ListItem>
                                                    <ListItemText secondary={`${t('applicants.bulk.andMore')} ${uploadResult.failed.length - 10} ${t('applicants.bulk.more')}`} />
                                                </ListItem>
                                            )}
                                        </List>
                                    </Paper>
                                </Box>
                            )}
                        </Box>
                    )}
                </Box>
            </DialogContent>

            <DialogActions>
                {step === 1 && (
                    <>
                        <Button onClick={handleClose} disabled={loading}>
                            {t('applicants.bulk.cancel')}
                        </Button>
                        <Button
                            onClick={handleProcessFile}
                            variant="contained"
                            disabled={!file || loading}
                        >
                            {t('applicants.bulk.processFile')}
                        </Button>
                    </>
                )}

                {step === 2 && (
                    <>
                        <Button onClick={handleBack} disabled={loading}>
                            {t('applicants.bulk.goBack')}
                        </Button>
                        <Button
                            onClick={handleConfirmUpload}
                            variant="contained"
                            disabled={loading}
                            color="success"
                        >
                            {t('applicants.bulk.confirmAndLoad')} {previewData?.validRows} {t('applicants.bulk.applicants')}
                        </Button>
                    </>
                )}

                {step === 3 && (
                    <Button onClick={handleClose} variant="contained">
                        {t('close')}
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default ApplicantBulkUploadModal;